import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppNavigationContext } from '@kizenapps/engine/react';

export interface GeneratedFilter {
  filterName?: string;
  filterConfig: Record<string, unknown>;
}

interface UseGeneratedFilterNavigationOptions {
  pageReady: boolean;
  // Page-specific handling when a generated filter arrives, e.g. resetting
  // filter/group state to an unsaved state and opening the filter menu
  onApply: () => void;
}

// Consumes an unsaved filter handed off through the app navigation context
// (e.g. from an AI chat preview card) and exposes it as a pending generated
// filter for ClientFilterDropdown to apply via applyGeneratedFilter
export const useGeneratedFilterNavigation = ({
  pageReady,
  onApply,
}: UseGeneratedFilterNavigationOptions) => {
  const { pathname, search } = useLocation();
  const [navContext, clearNavContext] = useAppNavigationContext(
    `${pathname}${search}`
  );

  const [generatedFilter, setGeneratedFilter] =
    useState<GeneratedFilter | null>(null);
  const clearGeneratedFilter = useCallback(() => {
    setGeneratedFilter(null);
  }, []);

  useEffect(() => {
    if (!pageReady || !navContext?.unsavedFilter) return;
    onApply();
    setGeneratedFilter({
      filterName: navContext.filterName as string | undefined,
      filterConfig: navContext.unsavedFilter as Record<string, unknown>,
    });
    clearNavContext();
  }, [pageReady, navContext, clearNavContext, onApply]);

  return { generatedFilter, clearGeneratedFilter };
};
